// utils/sbcTables.js — pure helpers for the Admin Board "SBC Tax Tables" screen
// (kailux-fixes-log.md §104). No React, no network: everything here is unit-tested.
//
// A table is stored as bands [{lower, upper, base, rate}] (rate is a fraction, e.g. 0.07).
// The form edits them as ROWS of strings [{lower, base, ratePct}] — `upper` is never typed,
// it is always the next row's `lower` (so a gap/overlap between bands is impossible to enter).
// The server (sbc_tax.py) is the authority and re-validates everything.

export function fmtRand(n) {
  const v = Number(n)
  if (!Number.isFinite(v)) return '—'
  // Locale-independent on purpose: matches the backend workings text and the PDF (R1,470.91), and does
  // not change with the admin's browser locale (en-ZA would print R1 470,91).
  return 'R' + v.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

function ratePctText(rate) {
  const pct = Math.round(Number(rate) * 100 * 1e6) / 1e6
  return String(pct)
}

export function bandsToRows(bands) {
  return (bands || []).map((b) => ({
    lower: String(b.lower ?? ''), base: String(b.base ?? ''), ratePct: ratePctText(b.rate ?? 0),
  }))
}

const num = (s) => (s === '' || s === null || s === undefined ? NaN : Number(s))

// rows -> API bands. Returns { bands, error }: error is a plain-language reason if a cell is not a number.
export function rowsToBands(rows) {
  const bands = []
  for (let i = 0; i < rows.length; i++) {
    const lower = num(rows[i].lower), base = num(rows[i].base), pct = num(rows[i].ratePct)
    if (![lower, base, pct].every(Number.isFinite)) {
      return { bands: null, error: `Band ${i + 1}: lower, base tax and rate must all be numbers.` }
    }
    const next = rows[i + 1]
    const upper = next === undefined ? null : num(next.lower)
    if (next !== undefined && !Number.isFinite(upper)) {
      return { bands: null, error: `Band ${i + 2}: lower must be a number.` }
    }
    bands.push({ lower, upper, base, rate: Number((pct / 100).toFixed(6)) })
  }
  return { bands, error: null }
}

// Fill each band's printed base from the previous band's formula (rounded to the rand, as SARS prints it).
// A convenience only — the admin should still compare with the SARS page; the server re-checks every base.
export function suggestBases(rows) {
  const out = rows.map((r) => ({ ...r }))
  if (out.length) out[0].base = '0'
  for (let i = 1; i < out.length; i++) {
    const pl = num(out[i - 1].lower), pb = num(out[i - 1].base), pr = num(out[i - 1].ratePct) / 100, l = num(out[i].lower)
    if ([pl, pb, pr, l].every(Number.isFinite)) out[i].base = String(Math.round(pb + pr * (l - pl)))
  }
  return out
}

// UTC date arithmetic on 'YYYY-MM-DD' strings. Built from Date.UTC and read back as UTC, so it is
// immune to the local-timezone off-by-one that bit period dates before (§84).
export function addDaysISO(iso, days) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
  if (!m) return ''
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) + days * 86400000
  return new Date(t).toISOString().slice(0, 10)
}

export function addYearsISO(iso, years) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
  if (!m) return ''
  const y = Number(m[1]) + years, mo = Number(m[2]), d = Number(m[3])
  const lastDay = new Date(Date.UTC(y, mo, 0)).getUTCDate() // day 0 of next month = last day of this month
  return `${y}-${String(mo).padStart(2, '0')}-${String(Math.min(d, lastDay)).padStart(2, '0')}`
}

// The next table normally starts the day after the latest confirmed one ends and runs a year.
export function nextYearDefaults(tables) {
  const confirmed = (tables || []).filter((t) => t.status === 'confirmed')
  if (!confirmed.length) return { ends_from: '', ends_to: '', bands: null }
  const latest = confirmed.reduce((a, b) => (a.ends_to >= b.ends_to ? a : b))
  const from = addDaysISO(latest.ends_to, 1)
  return { ends_from: from, ends_to: addYearsISO(addDaysISO(from, -1), 1) || '', bands: latest.bands }
}

export function summariseBands(bands) {
  if (!bands || bands.length === 0) return '—'
  return bands.map((b, i) => {
    const pct = `${ratePctText(b.rate)}%`
    return b.upper === null || b.upper === undefined
      ? `${pct} above ${fmtRand(b.lower)}`
      : `${pct} to ${fmtRand(b.upper)}`
  }).join(' · ')
}

export const COVERAGE_STYLE = {
  ok: { label: 'Covered', tone: 'ok' },
  warning: { label: 'Table running out', tone: 'warning' },
  critical: { label: 'Action needed', tone: 'critical' },
}

// FastAPI returns detail as a string (our errors) or a list of {loc,msg} (request validation).
export function errorText(err, fallback) {
  const d = err?.response?.data?.detail
  if (typeof d === 'string' && d) return d
  if (Array.isArray(d) && d.length) return d.map((x) => x?.msg || String(x)).join('; ')
  return fallback
}
